// Bridge: expõe `window.tripsApi` para o home.js legado (vanilla, em static/js/).
//
// O home.js legado não é um módulo Vite — ele roda como script clássico e
// não pode `import` Firebase SDK. Este módulo é o ponto de contato:
//  1. É carregado por home.html como <script type="module"> ANTES do home.js
//     (home.js tem `defer`, módulos também → a ordem de execução respeita
//     a ordem das tags porque ambos são deferred).
//  2. Roda depois do guard de auth (home.html importa authGuard primeiro e
//     aguarda ensureAuthenticated() resolver).
//  3. Pendura handlers em `window.tripsApi` → o home.js legado chama isso no
//     lugar de fetch('/api/travels*').
//
// Esse bridge existe temporariamente. Quando o home.js for migrado pra um
// módulo Vite real (feature/home/index.js), esse arquivo some.

import {
  listMyTrips,
  upsertTrip,
  deleteTripWithChildren,
} from '../../core/firestore.js';
import { authFetch, waitForAuth } from '../../core/auth.js';
import { auth } from '../../core/firebase.js';
import { initLucide, lucideCreateIcons } from '../lucide/index.js';
import { generateBlankDaysFromTripDates } from '../roteiro/api.js';
import { cloudLoad, cloudSave } from '../../core/cloudSync.js';

// Inicializa lucide icons assim que o bridge carrega (home.html não importa
// main.js, então não passa pelo initLucide padrão).
initLucide();

// Re-render após loadTrips (mudança no DOM) precisa re-varrer ícones
globalThis.refreshLucideIcons = () => { try { lucideCreateIcons(); } catch(e) {} };

// Expõe o authFetch pro home.js legado (script clássico, não pode importar
// core/auth). Rotas /api/* exigem Authorization: Bearer <idToken> — fetch puro
// toma 401 (ex.: /api/config, que entrega a MAPS_API_KEY do Places).
globalThis.authFetch = authFetch;

/**
 * Lista viagens do usuário logado.
 * Retorna array no shape que o home.js espera (mesmo do antigo /api/travels).
 * Também popula `window.__currentUid` pra o home.js clássico saber quem é o
 * usuário (sem depender do __firebase que só existe em dev).
 */
async function list() {
  const user = auth.currentUser || await waitForAuth();
  if (user) globalThis.__currentUid = user.uid;
  return await listMyTrips();
}

/**
 * Cria/atualiza uma viagem. `trip.id` é opcional — se ausente, gera um.
 * Retorna o objeto salvo (inclui userId, createdAt, updatedAt carimbados).
 *
 * Side effect novo: pra trips RECÉM-CRIADAS (sem id no payload) que tenham
 * startDate+endDate, geramos imediatamente a timeline com "Dia 1..N" e
 * gravamos no Firestore. Assim a viagem nasce com os dias prontos — quando
 * o user abrir /roteiro.html ou /, já vê o calendário.
 *
 * Pra UPDATES (id presente), NÃO regenera: poderia clobberar dias já
 * editados pelo usuário. Mudança de datas é tratada manualmente.
 *
 * Falha na geração da timeline NÃO derruba a criação da viagem — só loga
 * warning. O lazy auto-gen em loadRoteiro pega o caso defensivamente.
 */
async function create(trip) {
  const isNew = !trip?.id;
  const saved = await upsertTrip(trip);

  if (isNew && saved?.id && saved.startDate && saved.endDate) {
    try {
      // Defensivo: só gera se a timeline ainda está vazia. Em teoria sempre
      // está (acabou de criar), mas se houver retry/colisão evita sobrescrever.
      const existing = await cloudLoad(saved.id, 'timeline');
      const hasDays = Array.isArray(existing?.days) && existing.days.length > 0;
      if (!hasDays) {
        const days = generateBlankDaysFromTripDates(saved);
        if (days.length) {
          await cloudSave(saved.id, 'timeline', { days, _ts: Date.now() });
          console.info('[tripsApi] timeline pré-gerada com', days.length, 'dias pra trip', saved.id);
        }
      }
    } catch (err) {
      console.warn('[tripsApi] timeline pré-geração falhou (lazy fallback vai pegar):', err);
    }
  }

  return saved;
}

/**
 * Deleta a viagem + todas as sub-coleções (timeline, hotels, routes, etc).
 * Segurança: Firestore Rules só deixam o dono deletar.
 */
async function remove(id) {
  await deleteTripWithChildren(id);
}

// Expõe no global para o home.js legado consumir.
// Além do objeto, exponho também `tripsApiReady` — uma Promise que resolve
// quando o bridge está pronto. Isso elimina qualquer race entre esse módulo
// (defer) e o classic-defer do home.js: se o home.js rodar antes, ele
// pode `await window.tripsApiReady` pra sincronizar.
globalThis.tripsApi = { list, create, delete: remove };

// Se já havia um resolver pendurado (home.js rodou primeiro e criou a
// Promise), resolve agora. Se ainda não, cria uma já resolvida pra quem
// chegar depois poder await sem pendurar.
if (typeof globalThis.__resolveTripsApiReady === 'function') {
  globalThis.__resolveTripsApiReady();
} else {
  globalThis.tripsApiReady = Promise.resolve();
}
